/**
 * Generic host handshake acknowledgement for an app embedded in an iframe.
 * See creditsvc `hostHandshake.ts` and the `iframe-host-ack` skill.
 */

const READY_PING_TYPE = "iframe:ready";
const PROTOCOL_VERSION = 1;
const HELLO_TYPE = /:hello$/i;

export interface HostHelloMessage {
  type: string;
  version?: number;
  payload: unknown;
}

export interface HostHandshakeOptions {
  readyPingType?: string;
  onHello?: (message: HostHelloMessage, origin: string) => void;
  host?: Window;
}

function toOrigin(value: string | null | undefined): string | undefined {
  if (!value) return undefined;
  try {
    return new URL(value).origin;
  } catch {
    return undefined;
  }
}

export function resolveEmbedderOrigin(): string | undefined {
  const ancestors = window.location.ancestorOrigins as DOMStringList | undefined;
  return toOrigin(ancestors?.[0]) ?? toOrigin(document.referrer);
}

function isHelloMessage(data: unknown): data is HostHelloMessage {
  if (typeof data !== "object" || data === null) return false;
  const { type, payload } = data as { type?: unknown; payload?: unknown };
  return typeof type === "string" && HELLO_TYPE.test(type) && Boolean(payload);
}

let teardown: (() => void) | null = null;

export function initHostHandshake(options: HostHandshakeOptions = {}): () => void {
  if (teardown) return teardown;

  const host = options.host ?? window.parent;
  if (host === window) return () => {};

  const embedderOrigin = resolveEmbedderOrigin();
  if (embedderOrigin) {
    host.postMessage(
      { type: options.readyPingType ?? READY_PING_TYPE, version: PROTOCOL_VERSION },
      embedderOrigin,
    );
  }

  const onMessage = (event: MessageEvent): void => {
    if (event.source !== host) return;
    const data: unknown = event.data;
    if (!isHelloMessage(data)) return;

    // A hello payload carries customer name, address, phone and email, so it must never reach a
    // production console (workspace rule `14-logging-observability`).
    if (import.meta.env.DEV) {
      console.log("[iframe-host-ack]", event.origin, data);
    }

    host.postMessage(
      {
        type: data.type.replace(/hello$/i, "ready"),
        version: data.version ?? PROTOCOL_VERSION,
        payload: {},
      },
      event.origin,
    );

    options.onHello?.(data, event.origin);
  };

  window.addEventListener("message", onMessage);
  teardown = () => {
    window.removeEventListener("message", onMessage);
    teardown = null;
  };
  return teardown;
}
